import React, { useState } from 'react';
import { Search, ArrowRight, Truck, Package, Hammer, Forklift, Anchor, RotateCcw, CheckCircle2, AlertCircle, Clock, Plus, Trash2, X } from 'lucide-react';
// Predefined rates
const LABOUR_RATES: Record<string, number> = {
  'Jeep/BullCart': 300,
  Truck: 500,
  Bus: 600,
  Trailer: 800,
  Others: 400
};
const HOURLY_RATES: Record<string, number> = {
  CRANE: 1200,
  FORKLIFT: 800
};
interface DestinationVehicle {
  id: string;
  gatepassNumber: string;
  vehicleNo: string;
  commodity: string;
  vehicleType: string;
  status: 'Empty' | 'Partial/Loaded';
  postStatus: 'Loaded' | 'Still Empty/Partial'; // Status after loading
  mode: string;
  timeUsed: string;
  cost: number;
}
export function OperationsCharges() {
  // State
  const [sourceQuery, setSourceQuery] = useState('');
  const [sourceData, setSourceData] = useState<any>(null);
  const [sourcePostStatus, setSourcePostStatus] = useState<'Empty' | 'Still Loaded'>('Empty');
  // Destination List State
  const [destinations, setDestinations] = useState<DestinationVehicle[]>([]);
  const [isAddingDest, setIsAddingDest] = useState(false);
  // Temp state for adding new destination
  const [newDestQuery, setNewDestQuery] = useState('');
  const [newDestData, setNewDestData] = useState<any>(null);
  const [newDestMode, setNewDestMode] = useState('LABOUR');
  const [newDestTime, setNewDestTime] = useState('');
  const [newDestStatus, setNewDestStatus] = useState<'Empty' | 'Partial/Loaded'>('Empty');
  const [newDestPostStatus, setNewDestPostStatus] = useState<'Loaded' | 'Still Empty/Partial'>('Loaded');
  const [transferStatus, setTransferStatus] = useState<'idle' | 'success'>('idle');
  // Mock Search Handlers
  const handleSourceSearch = () => {
    if (!sourceQuery) return;
    setSourceData({
      gatepassNumber: 'KVT-1033-2082/83',
      vehicleNo: 'WB73G9558',
      commodity: 'FRUITS/VEGETABLES',
      vehicleType: 'TRUCK',
      status: 'Loaded',
      category: 'Truck'
    });
    setSourcePostStatus('Empty'); // Default assumption: it becomes empty
    setTransferStatus('idle');
  };
  const handleNewDestSearch = () => {
    if (!newDestQuery) return;
    setNewDestData({
      gatepassNumber: 'KVT-1013-2082/83',
      vehicleNo: 'WB03D2516',
      commodity: 'MACHINERY',
      vehicleType: 'TRUCK'
    });
  };
  const calculateItemCost = (mode: string, time: string, category: string) => {
    if (mode === 'LABOUR') {
      return LABOUR_RATES[category] || 0;
    } else {
      const hours = parseFloat(time) || 0;
      const rate = HOURLY_RATES[mode] || 0;
      return hours * rate;
    }
  };
  const handleAddDestination = () => {
    if (!newDestData) return;
    const cost = calculateItemCost(newDestMode, newDestTime, 'Truck'); // Assuming Truck category for now
    const newVehicle: DestinationVehicle = {
      id: Math.random().toString(36).substr(2, 9),
      gatepassNumber: newDestData.gatepassNumber,
      vehicleNo: newDestData.vehicleNo,
      commodity: newDestData.commodity,
      vehicleType: newDestData.vehicleType,
      status: newDestStatus,
      postStatus: newDestPostStatus,
      mode: newDestMode,
      timeUsed: newDestTime,
      cost: cost
    };
    setDestinations([...destinations, newVehicle]);
    // Reset add form
    setIsAddingDest(false);
    setNewDestQuery('');
    setNewDestData(null);
    setNewDestMode('LABOUR');
    setNewDestTime('');
    setNewDestStatus('Empty');
    setNewDestPostStatus('Loaded');
  };
  const handleRemoveDestination = (id: string) => {
    setDestinations(destinations.filter(d => d.id !== id));
  };
  const handleTransfer = () => {
    if (!sourceData || destinations.length === 0) return;
    setTransferStatus('success');
    setTimeout(() => {
      setTransferStatus('idle');
      // Here you would update the status of destinations to 'Loaded'
    }, 3000);
  };
  const handleReset = () => {
    setSourceQuery('');
    setSourceData(null);
    setSourcePostStatus('Empty');
    setDestinations([]);
    setIsAddingDest(false);
    setTransferStatus('idle');
  };
  const totalCost = destinations.reduce((sum, d) => sum + d.cost, 0);
  return <div className="flex flex-col gap-8 w-full h-full pb-24 max-w-7xl mx-auto" data-id="element-600">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-601">
        <div data-id="element-602">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-603">
            Gatepass Operations
          </h1>
          <p className="text-gray-500 text-sm mt-1" data-id="element-604">
            Manage cargo transfer between gatepasses efficiently.
          </p>
        </div>

        {transferStatus === 'success' && <div className="flex items-center gap-2 bg-green-50 text-green-700 px-4 py-2 rounded-full border border-green-200 animate-in fade-in slide-in-from-top-4 duration-300" data-id="element-605">
            <CheckCircle2 size={18} data-id="element-606" />
            <span className="font-medium text-sm" data-id="element-607">Transfer Successful!</span>
          </div>}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto_1fr] gap-8 items-start" data-id="element-608">
        {/* LEFT COLUMN: SOURCE */}
        <div className="flex flex-col gap-6" data-id="element-609">
          {/* Source Search Card */}
          <div className={`bg-white p-6 rounded-xl shadow-sm border transition-all duration-300 ${sourceData ? 'border-orange-200 ring-1 ring-orange-100' : 'border-gray-200'}`} data-id="element-610">
            <div className="flex items-center gap-3 mb-4" data-id="element-611">
              <div className="bg-orange-100 p-2 rounded-lg text-orange-600" data-id="element-612">
                <Package size={20} data-id="element-613" />
              </div>
              <h2 className="text-lg font-bold text-gray-800" data-id="element-614">Source (From)</h2>
            </div>

            <div className="flex gap-2" data-id="element-615">
              <div className="relative flex-1" data-id="element-616">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} data-id="element-617" />
                <input type="text" value={sourceQuery} onChange={e => setSourceQuery(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleSourceSearch()} placeholder="Enter Source Gatepass No." className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none transition-all" data-id="element-618" />
              </div>
              <button onClick={handleSourceSearch} className="bg-gray-900 hover:bg-gray-800 text-white px-4 py-2.5 rounded-lg font-medium transition-colors" data-id="element-619">
                Find
              </button>
            </div>
          </div>

          {/* Source Details Card */}
          {sourceData && <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-620">
              <div className="flex justify-between items-start mb-6" data-id="element-621">
                <div data-id="element-622">
                  <span className="text-xs font-bold text-gray-400 uppercase tracking-wider" data-id="element-623">
                    Gatepass Number
                  </span>
                  <p className="text-lg font-mono font-bold text-gray-900 mt-1" data-id="element-624">
                    {sourceData.gatepassNumber}
                  </p>
                </div>
                <span className="bg-green-100 text-green-700 text-xs font-bold px-2.5 py-1 rounded-full border border-green-200" data-id="element-625">
                  {sourceData.status}
                </span>
              </div>

              <div className="space-y-4" data-id="element-626">
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-100" data-id="element-627">
                  <span className="text-xs font-medium text-gray-500 flex items-center gap-1.5 mb-1" data-id="element-628">
                    <Truck size={14} data-id="element-629" /> Vehicle Number
                  </span>
                  <p className="font-bold text-gray-800" data-id="element-630">
                    {sourceData.vehicleNo}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4" data-id="element-631">
                  <div className="p-4 bg-orange-50 rounded-lg border border-orange-100" data-id="element-632">
                    <span className="text-xs font-medium text-orange-600 mb-1 block" data-id="element-633">
                      Commodity
                    </span>
                    <p className="font-bold text-gray-900 text-sm" data-id="element-634">
                      {sourceData.commodity}
                    </p>
                  </div>
                  <div className="p-4 bg-blue-50 rounded-lg border border-blue-100" data-id="element-635">
                    <span className="text-xs font-medium text-blue-600 mb-1 block" data-id="element-636">
                      Vehicle Type
                    </span>
                    <p className="font-bold text-gray-900 text-sm" data-id="element-637">
                      {sourceData.vehicleType}
                    </p>
                  </div>
                </div>

                {/* Post-Operation Status Selection */}
                <div className="pt-4 border-t border-gray-100" data-id="element-638">
                  <label className="text-xs font-bold text-gray-500 uppercase block mb-2" data-id="element-639">
                    Status After Unloading
                  </label>
                  <div className="flex gap-2" data-id="element-640">
                    {['Empty', 'Still Loaded'].map(status => <button key={status} onClick={() => setSourcePostStatus(status as any)} className={`flex-1 py-2 text-xs font-bold rounded-md border transition-all ${sourcePostStatus === status ? 'bg-orange-50 border-orange-500 text-orange-700 shadow-sm' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'}`} data-id="element-641">
                        {status}
                      </button>)}
                  </div>
                </div>
              </div>
            </div>}
        </div>

        {/* CENTER: DIRECTION INDICATOR */}
        <div className="hidden lg:flex flex-col items-center justify-center h-full pt-20" data-id="element-642">
          <div className={`p-3 rounded-full transition-all duration-500 ${sourceData && destinations.length > 0 ? 'bg-orange-500 text-white shadow-lg scale-110' : 'bg-gray-100 text-gray-400'}`} data-id="element-643">
            <ArrowRight size={32} data-id="element-644" />
          </div>
        </div>

        {/* RIGHT COLUMN: DESTINATIONS */}
        <div className={`flex flex-col gap-6 transition-opacity duration-500 ${sourceData ? 'opacity-100' : 'opacity-50 pointer-events-none'}`} data-id="element-645">
          <div className="flex items-center justify-between" data-id="element-646">
            <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2" data-id="element-647">
              <Truck className="text-blue-600" size={20} data-id="element-648" />
              Destinations (To)
            </h2>
            <button onClick={() => setIsAddingDest(true)} disabled={isAddingDest} className="flex items-center gap-1 text-sm font-bold text-blue-600 hover:text-blue-700 disabled:opacity-50" data-id="element-649">
              <Plus size={16} data-id="element-650" /> Add Vehicle
            </button>
          </div>

          {/* List of Added Destinations */}
          <div className="space-y-4" data-id="element-651">
            {destinations.map(dest => <div key={dest.id} className="bg-white p-4 rounded-xl shadow-sm border border-blue-100 relative group" data-id="element-652">
                <button onClick={() => handleRemoveDestination(dest.id)} className="absolute top-3 right-3 text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all" data-id="element-653">
                  <Trash2 size={16} data-id="element-654" />
                </button>

                <div className="flex justify-between items-start mb-3 pr-6" data-id="element-655">
                  <div data-id="element-656">
                    <span className="text-xs font-bold text-gray-400 uppercase" data-id="element-657">
                      Gatepass
                    </span>
                    <p className="font-mono font-bold text-gray-900 text-sm" data-id="element-658">
                      {dest.gatepassNumber}
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-1" data-id="element-659">
                    <span className="text-[10px] text-gray-400 font-medium uppercase" data-id="element-660">
                      Initial
                    </span>
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full border ${dest.status === 'Empty' ? 'bg-green-50 text-green-700 border-green-100' : 'bg-yellow-50 text-yellow-700 border-yellow-100'}`} data-id="element-661">
                      {dest.status}
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-4 text-xs text-gray-600 bg-gray-50 p-2 rounded-lg mb-2" data-id="element-662">
                  <span className="flex items-center gap-1" data-id="element-663">
                    <Truck size={12} data-id="element-664" /> {dest.vehicleNo}
                  </span>
                  <span className="flex items-center gap-1" data-id="element-665">
                    {dest.mode === 'LABOUR' ? <Hammer size={12} data-id="element-666" /> : dest.mode === 'CRANE' ? <Anchor size={12} data-id="element-667" /> : <Forklift size={12} data-id="element-668" />}
                    {dest.mode}
                  </span>
                  <span className="font-bold ml-auto text-gray-900" data-id="element-669">
                    NPR {dest.cost.toLocaleString()}
                  </span>
                </div>

                <div className="flex justify-between items-center pt-2 border-t border-gray-100" data-id="element-670">
                  <span className="text-[10px] font-bold text-gray-400 uppercase" data-id="element-671">
                    Status After Loading
                  </span>
                  <span className={`text-xs font-bold ${dest.postStatus === 'Loaded' ? 'text-blue-600' : 'text-orange-600'}`} data-id="element-672">
                    {dest.postStatus}
                  </span>
                </div>
              </div>)}

            {destinations.length === 0 && !isAddingDest && <div className="flex flex-col items-center justify-center h-32 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200 text-gray-400" data-id="element-673">
                <p className="text-sm font-medium" data-id="element-674">
                  No destination vehicles added.
                </p>
                <button onClick={() => setIsAddingDest(true)} className="text-blue-600 font-bold text-sm mt-2 hover:underline" data-id="element-675">
                  Add one now
                </button>
              </div>}
          </div>

          {/* Add New Destination Form */}
          {isAddingDest && <div className="bg-white p-6 rounded-xl shadow-lg border border-blue-200 ring-4 ring-blue-50/50 animate-in fade-in slide-in-from-bottom-2" data-id="element-676">
              <div className="flex justify-between items-center mb-4" data-id="element-677">
                <h3 className="font-bold text-gray-800" data-id="element-678">
                  Add Destination Vehicle
                </h3>
                <button onClick={() => setIsAddingDest(false)} className="text-gray-400 hover:text-gray-600" data-id="element-679">
                  <X size={18} data-id="element-680" />
                </button>
              </div>

              <div className="space-y-4" data-id="element-681">
                <div className="flex gap-2" data-id="element-682">
                  <input type="text" value={newDestQuery} onChange={e => setNewDestQuery(e.target.value)} placeholder="Search Gatepass No." className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none" data-id="element-683" />
                  <button onClick={handleNewDestSearch} className="bg-gray-900 text-white px-3 py-2 rounded-lg text-sm font-bold" data-id="element-684">
                    Find
                  </button>
                </div>

                {newDestData && <div className="space-y-4 pt-2 border-t border-gray-100" data-id="element-685">
                    <div className="grid grid-cols-2 gap-4 text-sm" data-id="element-686">
                      <div data-id="element-687">
                        <span className="text-xs text-gray-500 block" data-id="element-688">
                          Vehicle No
                        </span>
                        <span className="font-bold text-gray-900" data-id="element-689">
                          {newDestData.vehicleNo}
                        </span>
                      </div>
                      <div data-id="element-690">
                        <span className="text-xs text-gray-500 block" data-id="element-691">
                          Type
                        </span>
                        <span className="font-bold text-gray-900" data-id="element-692">
                          {newDestData.vehicleType}
                        </span>
                      </div>
                    </div>

                    <div data-id="element-693">
                      <label className="text-xs font-bold text-gray-500 uppercase block mb-2" data-id="element-694">
                        Current Status
                      </label>
                      <div className="flex gap-2" data-id="element-695">
                        {['Empty', 'Partial/Loaded'].map(status => <button key={status} onClick={() => setNewDestStatus(status as any)} className={`flex-1 py-1.5 text-xs font-bold rounded-md border ${newDestStatus === status ? 'bg-blue-50 border-blue-500 text-blue-700' : 'bg-white border-gray-200 text-gray-600'}`} data-id="element-696">
                            {status}
                          </button>)}
                      </div>
                    </div>

                    <div data-id="element-697">
                      <label className="text-xs font-bold text-gray-500 uppercase block mb-2" data-id="element-698">
                        Operation Mode
                      </label>
                      <div className="grid grid-cols-3 gap-2" data-id="element-699">
                        {['LABOUR', 'CRANE', 'FORKLIFT'].map(mode => <button key={mode} onClick={() => {
                    setNewDestMode(mode);
                    setNewDestTime('');
                  }} className={`py-2 text-xs font-bold rounded-md border ${newDestMode === mode ? 'bg-orange-50 border-orange-500 text-orange-700' : 'bg-white border-gray-200 text-gray-600'}`} data-id="element-700">
                            {mode}
                          </button>)}
                      </div>
                    </div>

                    {newDestMode !== 'LABOUR' && <div data-id="element-701">
                        <label className="text-xs font-bold text-gray-500 uppercase block mb-1" data-id="element-702">
                          Time Used (Hrs)
                        </label>
                        <input type="number" value={newDestTime} onChange={e => setNewDestTime(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm outline-none focus:border-orange-500" placeholder="e.g. 1.5" data-id="element-703" />
                      </div>}

                    <div data-id="element-704">
                      <label className="text-xs font-bold text-gray-500 uppercase block mb-2" data-id="element-705">
                        Status After Loading
                      </label>
                      <div className="flex gap-2" data-id="element-706">
                        {['Loaded', 'Still Empty/Partial'].map(status => <button key={status} onClick={() => setNewDestPostStatus(status as any)} className={`flex-1 py-1.5 text-xs font-bold rounded-md border ${newDestPostStatus === status ? 'bg-green-50 border-green-500 text-green-700' : 'bg-white border-gray-200 text-gray-600'}`} data-id="element-707">
                            {status}
                          </button>)}
                      </div>
                    </div>

                    <button onClick={handleAddDestination} disabled={newDestMode !== 'LABOUR' && !newDestTime} className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-2.5 rounded-lg text-sm shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-708">
                      Add to Transfer List
                    </button>
                  </div>}
              </div>
            </div>}
        </div>
      </div>

      {/* Footer Action Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 shadow-lg z-10 lg:pl-80" data-id="element-709">
        <div className="max-w-7xl mx-auto flex justify-between items-center" data-id="element-710">
          <button onClick={handleReset} className="flex items-center gap-2 text-gray-600 hover:text-gray-900 font-medium px-4 py-2 rounded-lg hover:bg-gray-100 transition-colors" data-id="element-711">
            <RotateCcw size={18} data-id="element-712" />
            Reset Form
          </button>

          <div className="flex items-center gap-4" data-id="element-713">
            <div className="text-right hidden sm:block" data-id="element-714">
              <p className="text-xs text-gray-500" data-id="element-715">Total Estimated Cost</p>
              <p className="text-lg font-bold text-orange-600 font-mono" data-id="element-716">
                {destinations.length > 0 ? `NPR ${totalCost.toLocaleString()}` : '---'}
              </p>
            </div>
            <button onClick={handleTransfer} disabled={!sourceData || destinations.length === 0} className={`flex items-center gap-2 px-8 py-3 rounded-lg font-bold text-white shadow-md transition-all ${sourceData && destinations.length > 0 ? 'bg-orange-500 hover:bg-orange-600 hover:shadow-lg hover:-translate-y-0.5' : 'bg-gray-300 cursor-not-allowed'}`} data-id="element-717">
              Transfer Cargo ({destinations.length})
              <ArrowRight size={20} data-id="element-718" />
            </button>
          </div>
        </div>
      </div>
    </div>;
}